import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Avatar, EmptyState } from "../../components/ui";
import { Icon } from "../shared/components";
import {
  API, ensureArray, ensureObject, formatTime, formatMinutesAsLabel,
  getInitials, getSlaMeta, conversationStatusLabel, formatBrazilPhone,
} from "../shared/utils";

// ── Agent load card ───────────────────────────────────────────────────────────
function AgentCard({ agent, onConversationClick }) {
  const statusColor = agent.openLoad === 0 ? "var(--green)" : agent.openLoad >= 8 ? "var(--red)" : "var(--amber)";
  // Atendente "online" se tem conversa aberta agora; "away" se já atendeu hoje mas tá idle; "offline" se inativo
  const presence = agent.openLoad > 0 ? "online" : agent.active === 1 ? "away" : "offline";
  return (
    <div className="supervisorAgentCard">
      <div className="supervisorAgentHeader">
        <Avatar name={agent.name} className="photo" size="md" status={presence} />
        <div className="supervisorAgentInfo">
          <strong>{agent.name}</strong>
          <span>{agent.role}</span>
        </div>
        <div className="supervisorAgentLoad" style={{ color: statusColor }}>
          <strong>{agent.openLoad}</strong>
          <small>abertas</small>
        </div>
      </div>
      {agent.conversations.length > 0 && (
        <div className="supervisorAgentConvs">
          {agent.conversations.slice(0, 4).map((c) => {
            const sla = getSlaMeta(c);
            return (
              <button
                key={c.id}
                className={`supervisorConvChip sla-${sla.tone}`}
                onClick={() => onConversationClick(c)}
                title={`${c.contact_name} — ${sla.label}`}
              >
                <span className="supervisorConvName">{c.contact_name}</span>
                <span className={`supervisorConvSla ${sla.tone}`}>{sla.label}</span>
              </button>
            );
          })}
          {agent.conversations.length > 4 && (
            <span className="supervisorConvMore">+{agent.conversations.length - 4} mais</span>
          )}
        </div>
      )}
      {agent.openLoad === 0 && (
        <div className="supervisorAgentFree">Disponível para receber atendimentos</div>
      )}
    </div>
  );
}

// ── Reassign modal ────────────────────────────────────────────────────────────
function ReassignModal({ conversation, agents, onReassign, onClose, saving }) {
  const [target, setTarget] = useState("");
  return (
    <div className="modal-overlay-v2">
      <div className="modal-v2 modal-sm">
        <div className="modal-header">
          <h3>Redistribuir conversa</h3>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <Icon name="close" size={14} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-2)", marginBottom: 12 }}>
            Conversa de <strong>{conversation.contact_name}</strong>
          </p>
          <div className="agent-list">
            {agents.map((a) => (
              <div
                key={a.id}
                className={`agent-option${String(target) === String(a.id) ? " selected" : ""}`}
                onClick={() => setTarget(a.id)}
              >
                <Avatar name={a.name} size="sm" />
                <div>
                  <strong>{a.name}</strong>
                  <span>{a.openLoad} conversa(s) aberta(s)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary btn-sm" onClick={() => onReassign(target)} disabled={saving || !target}>
            {saving ? "Redirecionando…" : "Redirecionar"}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function SupervisorPage({ api, user, setActivePage }) {
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveAt, setLiveAt] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [reassigning, setReassigning] = useState(false);
  const [reassignModalConv, setReassignModalConv] = useState(null);
  const [notice, setNotice] = useState("");
  const streamRef = useRef(null);
  const reconnectRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [convRes, userRes, summaryRes] = await Promise.all([
        api.get("/conversations", { params: { limit: 200 } }),
        api.get("/users"),
        api.get("/reports/summary"),
      ]);
      // API retorna objeto paginado { conversations: [...], has_more, next_cursor }
      const convData = convRes.data;
      const convList = Array.isArray(convData) ? convData : ensureArray(convData.conversations);
      setConversations(convList);
      setUsers(ensureArray(userRes.data));
      setSummary(ensureObject(summaryRes.data));
      setLiveAt(new Date());
    } catch {}
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // SSE real-time
  useEffect(() => {
    let cancelled = false;
    async function connect() {
      try {
        const tokenRes = await api.post("/realtime/token");
        if (cancelled) return;
        const source = new EventSource(`${API}/realtime/stream?token=${encodeURIComponent(tokenRes.data.token)}`);
        streamRef.current = source;
        const refresh = () => { if (!cancelled) load(); };
        source.addEventListener("message_created", refresh);
        source.addEventListener("conversation_updated", refresh);
        source.onerror = () => { source.close(); if (!cancelled) reconnectRef.current = setTimeout(connect, 3000); };
      } catch {
        if (!cancelled) reconnectRef.current = setTimeout(connect, 3000);
      }
    }
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (streamRef.current) streamRef.current.close();
    };
  }, [api, load]);

  async function reassignConversation(toUserId) {
    if (!reassignModalConv || !toUserId) return;
    setReassigning(true);
    try {
      await api.patch(`/conversations/${reassignModalConv.id}/assign`, { assigned_to: toUserId });
      const agent = users.find((u) => String(u.id) === String(toUserId));
      setNotice(`Conversa de ${reassignModalConv.contact_name} redirecionada para ${agent?.name || "atendente"}.`);
      setReassignModalConv(null);
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao redirecionar.");
    } finally {
      setReassigning(false);
    }
  }

  // Build per-agent view
  const openConversations = useMemo(
    () => conversations.filter((c) => c.status !== "finalizado"),
    [conversations]
  );

  const agentView = useMemo(() => {
    const activeAgents = users.filter((u) => u.active === 1);
    return activeAgents.map((agent) => {
      const agentConvs = openConversations.filter((c) => Number(c.assigned_to) === Number(agent.id));
      return {
        ...agent,
        openLoad: agentConvs.length,
        conversations: agentConvs.sort((a, b) => {
          const slaA = getSlaMeta(a).tone;
          const slaB = getSlaMeta(b).tone;
          const order = { critical: 0, warning: 1, normal: 2 };
          return (order[slaA] ?? 3) - (order[slaB] ?? 3);
        }),
      };
    }).sort((a, b) => b.openLoad - a.openLoad);
  }, [users, openConversations]);

  const unassigned = useMemo(
    () => openConversations.filter((c) => !c.assigned_to),
    [openConversations]
  );

  const critical = useMemo(
    () => openConversations.filter((c) => getSlaMeta(c).tone === "critical"),
    [openConversations]
  );

  const kpis = [
    { label: "Abertas", value: openConversations.length, tone: "green" },
    { label: "Críticas (SLA)", value: critical.length, tone: critical.length > 0 ? "red" : "green" },
    { label: "Sem atendente", value: unassigned.length, tone: unassigned.length > 0 ? "amber" : "green" },
    { label: "Atendentes ativos", value: agentView.filter((a) => a.role !== "master").length, tone: "blue" },
    { label: "Tempo médio resp.", value: formatMinutesAsLabel(summary?.avg_first_response_minutes || 0), tone: "default" },
  ];

  return (
    <div className="supervisorPage">
      <div className="supervisorTopBar">
        <div>
          <h2>Supervisor</h2>
          <p>
            Monitoramento em tempo real •
            <span className={`supervisorLive ${liveAt ? "active" : ""}`}>
              {" "}⬤ {liveAt ? `Atualizado ${liveAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "Conectando…"}
            </span>
          </p>
        </div>
        <button className="ghostButton" onClick={load}>
          <Icon name="refresh" size={14} /> Atualizar
        </button>
      </div>

      {notice && <div className="sendNotice">{notice}</div>}

      <div className="supervisorKpis">
        {kpis.map((k) => (
          <div key={k.label} className={`supervisorKpi tone-${k.tone}`}>
            <span>{k.label}</span>
            <strong>{k.value}</strong>
          </div>
        ))}
      </div>

      {/* Unassigned conversations — needs immediate action */}
      {unassigned.length > 0 && (
        <div className="supervisorSection">
          <div className="supervisorSectionHeader">
            <h3><Icon name="bell" size={14} /> Sem atendente — {unassigned.length} conversa(s)</h3>
            <button className="ghostButton" onClick={() => setActivePage("atendimentos")}>Ver todas</button>
          </div>
          <div className="supervisorConvList">
            {unassigned.map((c) => {
              const sla = getSlaMeta(c);
              return (
                <div key={c.id} className={`supervisorConvRow sla-${sla.tone}`}>
                  <div className="avatar photo mini">{getInitials(c.contact_name)}</div>
                  <div className="supervisorConvInfo">
                    <strong>{c.contact_name}</strong>
                    <span>{c.last_message || "Sem mensagem"}</span>
                    <small className={sla.tone}>{sla.label} · {formatTime(c.updated_at)}</small>
                  </div>
                  <div className="supervisorConvControls">
                    <button className="secondaryBtn" onClick={() => setReassignModalConv(c)}>
                      Atribuir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Agent workload */}
      <div className="supervisorSection">
        <div className="supervisorSectionHeader">
          <h3><Icon name="people" size={14} /> Carga por atendente</h3>
        </div>
        {loading ? (
          <EmptyState icon="people" title="Carregando atendentes" text="Aguarde…" />
        ) : agentView.length === 0 ? (
          <EmptyState illustration="team" title="Nenhum usuário ativo" text="Cadastre atendentes em Configurações." />
        ) : (
          <div className="supervisorAgentGrid">
            {agentView.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onConversationClick={(c) => setReassignModalConv(c)}
              />
            ))}
          </div>
        )}
      </div>

      {reassignModalConv && (
        <ReassignModal
          conversation={reassignModalConv}
          agents={agentView}
          onReassign={reassignConversation}
          onClose={() => setReassignModalConv(null)}
          saving={reassigning}
        />
      )}
    </div>
  );
}

export default SupervisorPage;
